import React, { useState, useEffect } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import { db } from "../firebase"; // Import the Firestore instance
import AnimatedPage from "./AnimatedPage";
import PdfLogo from "./pdflogo-sleepingowls.png";
import jsPDF from "jspdf";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";

function GatePassForm() {
  const { t } = useLanguage();
  const { showToast } = useToast();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [gpNumber, setGPNumber] = useState("");
  const [showLogo, setShowLogo] = useState(true); // Logo toggle state

  // Modal state for invoice parser
  const [showModal, setShowModal] = useState(false);
  const [invoiceText, setInvoiceText] = useState("");
  const [parsingLoading, setParsingLoading] = useState(false);

  const [formInputs, setFormInputs] = useState({
    partyName: "",
    GPNo: "", // Use GPNo from state
    date: "",
    gpcomment: "",
    salesChannel: "",
    shippingMode: "",
    approvedBy: "", // Add approvedBy to state
    otherApprovedBy: "", // Add otherApprovedBy for manual input
    invoiceBy: "",
    otherInvoiceBy: "",
    items: [
      {
        boxnumber: "",
        itemName: "",
        packingStyle: "",
        quantity: "",
        rate: "",
        gst: "",
      },
    ],
  });

  useEffect(() => {
    // Set up a real-time listener for GP number
    const unsubscribe = db
      .collection("auto-gpnumber")
      .doc("doc-gpnumber")
      .onSnapshot((doc) => {
        if (doc.exists) {
          const currentGPNo = doc.data().gpnumber;
          setGPNumber(currentGPNo);
          setFormInputs((prevState) => ({
            ...prevState,
            GPNo: currentGPNo,
          }));
        } else {
          console.log("No such document!");
        }
      });

    return () => unsubscribe();
  }, []);

  // Effect for handling Duplicate Data
  useEffect(() => {
    if (location.state && location.state.duplicateData) {
      const dupData = location.state.duplicateData;

      // We want to pre-fill everything EXCEPT GPNo (which is handled by auto-gp mechanism)
      // and date (which should be today)
      setFormInputs(prev => ({
        ...prev,
        partyName: dupData.partyName || "",
        // GPNo is auto-set by the other useEffect
        date: "", // Will be set to today on submit
        gpcomment: dupData.comment || "",
        salesChannel: dupData.salesChannel || "",
        shippingMode: dupData.shippingMode || "",
        approvedBy: dupData.approvedBy || "",
        invoiceBy: dupData.invoiceBy || "",
        items: dupData.items ? dupData.items.map(item => ({
          boxnumber: item.boxnumber || "",
          itemName: item.itemName || "",
          packingStyle: item.packingStyle || "",
          quantity: item.quantity || "",
          rate: item.rate || "",
          gst: item.gst || "",
        })) : []
      }));
    }
  }, [location.state]);

  // Invoice parsing functions
  const extractCompanyName = (text) => {
    const lines = text.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line.toLowerCase() === 'bill to') {
        // Get the next non-empty line after "Bill To"
        for (let j = i + 1; j < lines.length; j++) {
          const nextLine = lines[j].trim();
          if (nextLine && nextLine !== '' && !nextLine.includes('Invoice Details')) {
            return nextLine;
          }
        }
      }
    }

    return '';
  };

  const parseItemGroup = (group) => {
    if (group.length < 2) return null;

    // Regex for Quantity + Unit (Supporting Decimals like 97.5)
    // Matches: whitespace + (number with optional decimal) + whitespace + Unit
    const strictRegex = /(?:^|\s)(\d+(\.\d+)?)\s+(MTR|PCS|PRS|-)/i;

    // Strategy 1: Look for Quantity followed by Unit (PCS, MTR, etc.)
    let dataLine = group.find(line => line.match(strictRegex));
    let quantity = "";
    let boxnumber = "";

    if (dataLine) {
      const quantityMatch = dataLine.match(strictRegex);
      quantity = quantityMatch ? quantityMatch[1] : '';

      // Strategy for Inline Bale No (e.g., "HSN BaleNo Qty Unit")
      // If we found a strict match, check what comes BEFORE it in the dataLine.
      if (quantityMatch) {
        // Get the text before the quantity match
        const beforeMatchText = dataLine.substring(0, quantityMatch.index).trim();
        if (beforeMatchText) {
          const tokens = beforeMatchText.split(/\s+/);
          // If we have at least 2 tokens before quantity (e.g., "520832 8405"),
          // assume standard format [HSN] [BaleNo]
          if (tokens.length >= 2) {
            // The last token before quantity is likely the Bale No
            boxnumber = tokens[tokens.length - 1];
          }
        }
      }

    } else {
      // Strategy 2: Fallback for lines without explicit unit but with Price (e.g., "5 ₹ 123.00")
      // Look for a line that contains the currency symbol '₹'
      dataLine = group.find(line => line.includes("₹"));

      if (dataLine) {
        // Attempt to extract the first number before the currency symbol. 
        // Support decimals here too just in case.
        const fallbackMatch = dataLine.match(/^(\d+(\.\d+)?)\s+₹/) || dataLine.match(/(\d+(\.\d+)?)\s+₹/);
        if (fallbackMatch) {
          quantity = fallbackMatch[1];
        }
      }
    }

    if (!dataLine) return null; // Cannot find essential data

    // Strategy for Bale No / Box No:
    // Look for any line that is purely numeric, is NOT the serial number (group[0]),
    // and is NOT the dataLine (though dataLine usually has text).
    // This handles the "Bale No" column edge case.
    // If boxnumber is not yet found by inline strategy, check isolated line strategy
    let baleLineToExclude = null;
    if (!boxnumber) {
      const baleNumberLine = group.find((line, index) =>
        index > 0 && // Not the first line (Serial No)
        line !== dataLine &&
        line.match(/^\d+$/)
      );
      if (baleNumberLine) {
        boxnumber = baleNumberLine;
        baleLineToExclude = baleNumberLine;
      }
    }

    // The item name is any line that is NOT the serial number (numeric), NOT the dataLine,
    // and NOT the newly found baleNumberLine (if any).
    const nameLines = group.filter(line =>
      !line.match(/^\d+$/) &&
      line !== dataLine &&
      line !== baleLineToExclude
    );
    const itemName = nameLines.join(' ').trim() || '';

    return {
      itemName: itemName,
      rate: "",
      quantity: quantity,
      boxnumber: boxnumber,
      packingStyle: "",
      gst: "",
    };
  };

  const extractItems = (text) => {
    const items = [];
    const lines = text.split('\n').map(line => line.trim());

    // Find the start and end markers for the item section
    const startIndex = lines.findIndex(line => line.includes('Item name') && line.includes('#'));
    if (startIndex === -1) return items;

    let endIndex = lines.findIndex((line, i) => i > startIndex && line.toLowerCase().startsWith('total'));
    if (endIndex === -1) endIndex = lines.length;

    const itemSectionLines = lines.slice(startIndex + 1, endIndex);

    let currentItemGroup = [];

    for (const line of itemSectionLines) {
      // A new item starts with a line that is only a number (and likely a short one).
      if (line.match(/^\d+$/) && line.length < 4) {
        if (currentItemGroup.length > 0) {
          // Process the previously collected group of lines for an item
          const item = parseItemGroup(currentItemGroup);
          if (item) items.push(item);
        }
        // Start a new group for the next item
        currentItemGroup = [line];
      } else if (line) { // Add other non-empty lines to the current group
        currentItemGroup.push(line);
      }
    }

    // Process the very last item group after the loop finishes
    if (currentItemGroup.length > 0) {
      const item = parseItemGroup(currentItemGroup);
      if (item) items.push(item);
    }

    return items;
  };

  const handleQuickGatepass = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setInvoiceText("");
  };

  const handleParseInvoice = () => {
    if (!invoiceText.trim()) {
      showToast(t("enterInvoiceAlert"), "warning");
      return;
    }

    setParsingLoading(true);

    try {
      // Extract company name and items
      const companyName = extractCompanyName(invoiceText);
      const extractedItems = extractItems(invoiceText);

      // Update form with parsed data
      setFormInputs(prevState => ({
        ...prevState,
        partyName: companyName,
        items: extractedItems.length > 0 ? extractedItems : [{
          boxnumber: "",
          itemName: "",
          packingStyle: "",
          quantity: "",
          rate: "",
          gst: "",
        }]
      }));

      // Close modal
      setShowModal(false);
      setInvoiceText("");

      // Show success message
      showToast(t("parseSuccessAlert", { count: extractedItems.length, company: companyName ? ` (${companyName})` : '' }), "success");

    } catch (error) {
      showToast('Error parsing invoice: ' + error.message, "danger");
      console.error('Parsing error:', error);
    }

    setParsingLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs({ ...formInputs, [name]: value });
  };

  const parseGPNumber = (value) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : null;
  };



  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();

    // Increment GP number locally before updating in Firestore
    const currentGPNo = parseGPNumber(formInputs.GPNo);
    if (currentGPNo === null) {
      setLoading(false);
      showToast("GP number is invalid. Please refresh and try again.", "danger");
      return;
    }
    const updatedGPNo = currentGPNo + 1;
    // Update GP No. in Firestore
    try {
      await db.collection("gatePasses").add({
        partyName: formInputs.partyName,
        // pinCode: formInputs.pinCode,
        salesChannel: formInputs.salesChannel,
        shippingMode: formInputs.shippingMode,
        GPNo: currentGPNo,
        date: new Date(),
        items: formInputs.items,
        // pdfName: `${formInputs.GPNo}-${formInputs.partyName}-GatePass.pdf`, // No longer storing PDF name for retrieval
        DeliveryStatus: "NotDelivered",
        comment: formInputs.gpcomment,
        approvedBy: formInputs.approvedBy === "Others" ? formInputs.otherApprovedBy : formInputs.approvedBy,
        invoiceBy: formInputs.invoiceBy === "Others" ? formInputs.otherInvoiceBy : formInputs.invoiceBy,
        // Add other data as needed
      });

      await db.collection("auto-gpnumber").doc("doc-gpnumber").update({
        gpnumber: updatedGPNo,
      });

      console.log("Data stored in Firestore");

      // Generate PDF locally
      generatePDF();

      setLoading(false);

      // Reload after short delay to allow PDF download to start
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error("Error storing data in Firestore:", error);
      setLoading(false);
      showToast("Error submitting form: " + error.message, "danger");
    }
  };

  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const data = {
        ...formInputs,
        comment: formInputs.gpcomment,
        date: new Date()
      };

      // --- HEADER ---
      if (showLogo) {
        doc.addImage(PdfLogo, "PNG", 14, 10, 60, 24);
      }

      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("GATE PASS", 196, 20, { align: "right" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`GP NO: ${data.GPNo}`, 196, 28, { align: "right" });

      const dateStr = data.date.toLocaleDateString("en-GB");
      const timeStr = data.date.toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' });
      doc.text(`Date: ${dateStr}`, 196, 33, { align: "right" });
      doc.text(`Time: ${timeStr}`, 196, 38, { align: "right" });

      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(14, 42, 196, 42);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("SHIP TO:", 14, 50);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(data.partyName || "N/A", 14, 56);

      const startYInfo = 64;

      const tableColumn = ["#", "Box No", "Item Name", "Packing", "Qty", "Rate", "GST"];
      const tableRows = (data.items || []).map((item, index) => [
        index + 1,
        item.boxnumber || "-",
        item.itemName || "-",
        item.packingStyle || "-",
        item.quantity || "0",
        item.rate || "-",
        item.gst || "-"
      ]);

      doc.autoTable({
        startY: startYInfo + 8,
        head: [tableColumn],
        body: tableRows,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 3, textColor: 0, overflow: 'linebreak' },
        headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', lineWidth: { bottom: 0.1 }, lineColor: 0 },
        bodyStyles: { lineWidth: { bottom: 0.1 }, lineColor: 220 },
        columnStyles: {
          0: { cellWidth: 10 },
          4: { halign: 'center' },
          5: { halign: 'right' },
          6: { halign: 'right' }
        }
      });

      let finalY = doc.lastAutoTable.finalY + 10;
      const rawTotalQty = (data.items || []).reduce((sum, item) => sum + (parseFloat(item.quantity || 0) || 0), 0);
      const totalQty = Number.isInteger(rawTotalQty) ? rawTotalQty : parseFloat(rawTotalQty.toFixed(3));
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(`Total Qty: ${totalQty}`, 196, finalY, { align: "right" });

      finalY += 10;
      if (data.comment) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.text(`Remarks: ${data.comment}`, 14, finalY);
        finalY += 10;
      }

      finalY += 20;
      if (finalY > doc.internal.pageSize.height - 40) {
        doc.addPage();
        finalY = 40;
      }
      doc.setLineWidth(0.1);

      const ySigLine = finalY + 15;
      doc.line(14, ySigLine, 60, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.fontSize = 8;
      doc.text("Account's Approval", 14, ySigLine + 5);

      if (data.approvedBy) {
        doc.setFont("helvetica", "normal");
        const approver = data.approvedBy === "Others" ? data.otherApprovedBy : data.approvedBy;
        doc.text(`Approved by : ${approver}`, 14, ySigLine + 16);
      }

      doc.line(75, ySigLine, 125, ySigLine);
      doc.setFont("helvetica", "bold");
      doc.text("Gate Approval", 75, ySigLine + 5);

      doc.line(140, ySigLine, 196, ySigLine);
      doc.text("Received By (Sign & Stamp)", 140, ySigLine + 5);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Name/Phone:", 140, ySigLine + 10);
      doc.text("Vehicle No:", 140, ySigLine + 15);

      const pdfBlob = doc.output("blob");
      // Open in new tab for print
      const blobURL = URL.createObjectURL(pdfBlob);
      window.open(blobURL, "_blank");

      // For saving to Firebas (in handleSubmit) - it calls this but logic was slightly different.
      // Actually handleSubmit calls this generatePDF() just to open/view it, OR it relies on saving?
      // Re-reading handleSubmit: it awaits db.add, then calls generatePDF(), then reload.
      // It DOES NOT save the PDF to storage in the original code? 
      // Original code: "pdfName" commented out. So it seems it just generates local blob for user to print?
      // "Generate PDF locally using the gatePass data"
      // Wait, original generatePDF code had `pdf.save` commented out and just window.open.
      // So this matches.

    } catch (error) {
      console.error("Error generating PDF:", error);
      showToast("Error generating PDF: " + error.message, "danger");
    }
  };

  const handleItemChange = (index, e) => {
    const updatedItems = formInputs.items.map((item, i) => {
      if (i === index) {
        return { ...item, [e.target.name]: e.target.value };
      }
      return item;
    });
    setFormInputs({ ...formInputs, items: updatedItems });
  };

  const addItem = () => {
    setFormInputs({
      ...formInputs,
      items: [
        ...formInputs.items,
        {
          boxnumber: "",
          itemName: "",
          packingStyle: "",
          quantity: "",
          rate: "",
          gst: "",
          gpComment: "",
        },
      ],
    });
  };

  const removeItem = (index) => {
    const updatedItems = [...formInputs.items];
    updatedItems.splice(index, 1);
    setFormInputs({ ...formInputs, items: updatedItems });
  };

  return (
    <AnimatedPage>
      <div className="min-h-screen py-8 px-4 md:px-8 relative" >
        {/* Patterned Background Layer */}
        < div className="fixed inset-0 h-full w-full -z-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" ></div>

        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold tracking-tight text-slate-800">{t("createNewGp")}</h2>
            <button
              onClick={handleQuickGatepass}
              className="inline-flex items-center justify-center rounded-xl bg-white border border-blue-100 px-4 py-2 text-sm font-semibold text-[#4580d6] hover:bg-blue-50 transition-all shadow-sm active:scale-95"
            >
              <i className="fa fa-bolt mr-2 text-blue-700 animate-pulse"></i> {t("quickGatepass")}
            </button>
          </div>

        {/* Main Form Card */}
        <div className="glass-card border border-white/60 rounded-3xl shadow-2xl shadow-blue-200/50 overflow-hidden relative">
          {/* GP Number Ribbon */}
          <div className="gp-ribbon">
            <span className="label">{t("gpNumber")}</span>
            <span id="gpDisplay">{gpNumber || "GP-..."}</span>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-10">
            {/* Primary Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 mb-10 pt-4">
              {/* Row 1 */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="partyName">{t("partyName")}</label>
                <input
                  type="text"
                  id="partyName"
                  name="partyName"
                  placeholder={t("enterPartyName")}
                  required
                  value={formInputs.partyName}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm placeholder:text-slate-400 form-input-focus shadow-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="salesChannel">{t("salesChannel")}</label>
                <select
                  id="salesChannel"
                  name="salesChannel"
                  required
                  value={formInputs.salesChannel}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus appearance-none cursor-pointer"
                >
                  <option value="">{t("selectChannel")}</option>
                  <option value="Factory (Job work etc.)">{t("factoryChannel")}</option>
                  <option value="Indiamart/Retail/Whitelabelling (Sneha & Kashish)">{t("indiamartChannel")}</option>
                  <option value="Website">{t("websiteChannel")}</option>
                  <option value="Operations (Rajesh & Raj)">{t("operationsChannel")}</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="shippingMode">{t("shippingMode")}</label>
                <select
                  id="shippingMode"
                  name="shippingMode"
                  required
                  value={formInputs.shippingMode}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus"
                >
                  <option value="">{t("selectMode")}</option>
                  <option value="Transport">{t("transportMode")}</option>
                  <option value="Courier">{t("courierMode")}</option>
                  <option value="Tempo / Porter">{t("tempoMode")}</option>
                  <option value="Customer Pickup">{t("pickupMode")}</option>
                </select>
              </div>

              {/* Row 2 */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="approvedBy">{t("approvedBy")}</label>
                <select
                  id="approvedBy"
                  name="approvedBy"
                  required
                  value={formInputs.approvedBy}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus"
                >
                  <option value="">{t("selectApprover")}</option>
                  <option value="Rajkumar">Rajkumar</option>
                  <option value="Rajesh">Rajesh</option>
                  <option value="Kashish">Kashish</option>
                  <option value="Sneha">Sneha</option>
                  <option value="Lucky">Lucky</option>
                  <option value="Others">{t("others")}</option>
                </select>
                {/* Additional input for 'Others' */}
                {formInputs.approvedBy === "Others" && (
                  <input
                    type="text"
                    name="otherApprovedBy"
                    placeholder={t("enterName")}
                    required
                    value={formInputs.otherApprovedBy}
                    onChange={handleInputChange}
                    className="flex h-12 w-full mt-2 rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm placeholder:text-slate-400 form-input-focus shadow-sm"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="invoiceBy">{t("invoiceBy")}</label>
                <select
                  id="invoiceBy"
                  name="invoiceBy"
                  required
                  value={formInputs.invoiceBy}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus"
                >
                  <option value="">{t("selectIssuer")}</option>
                  <option value="Rajkumar">Rajkumar</option>
                  <option value="Rajesh">Rajesh</option>
                  <option value="Kashish">Kashish</option>
                  <option value="Sneha">Sneha</option>
                  <option value="Lucky">Lucky</option>
                  <option value="Others">{t("others")}</option>
                </select>
                {/* Additional input for 'Others' */}
                {formInputs.invoiceBy === "Others" && (
                  <input
                    type="text"
                    name="otherInvoiceBy"
                    placeholder={t("enterName")}
                    required
                    value={formInputs.otherInvoiceBy}
                    onChange={handleInputChange}
                    className="flex h-12 w-full mt-2 rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm placeholder:text-slate-400 form-input-focus shadow-sm"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1" htmlFor="gpcomment">{t("comment")}</label>
                <input
                  type="text"
                  id="gpcomment"
                  name="gpcomment"
                  placeholder={t("addRemarks")}
                  value={formInputs.gpcomment}
                  onChange={handleInputChange}
                  className="flex h-12 w-full rounded-2xl border border-blue-50 bg-white/60 px-4 py-2 text-sm form-input-focus shadow-sm"
                />
              </div>
            </div>

            {/* Items Table Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold tracking-tight text-slate-700">{t("itemsDetails")}</h3>
              </div>

              <div className="table-container overflow-x-auto border border-blue-100 rounded-2xl bg-white/40">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-blue-50/80 text-blue-900/70 border-b border-blue-100">
                    <tr>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap">{t("boxNo")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap">{t("itemName")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap">{t("packingStyle")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap w-32 text-center">{t("qty")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap w-24">{t("rate")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap w-24">{t("gst")}</th>
                      <th className="px-6 py-4 font-bold uppercase tracking-tighter text-[11px] whitespace-nowrap text-center">{t("action")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-50/50">
                    {formInputs.items.map((item, index) => (
                      <tr key={index} className="hover:bg-white/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="#"
                            name="boxnumber"
                            value={item.boxnumber}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder={t("itemName")}
                            required
                            name="itemName"
                            value={item.itemName}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder={t("packingStyle")}
                            name="packingStyle"
                            value={item.packingStyle}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            placeholder="0"
                            name="quantity"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl text-center font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="0.00"
                            name="rate"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            placeholder="%"
                            name="gst"
                            value={item.gst}
                            onChange={(e) => handleItemChange(index, e)}
                            className="w-full bg-transparent border-none px-2 py-2 form-input-focus rounded-xl"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-slate-300 hover:text-rose-400 transition-colors p-2"
                          >
                            <i className="fa fa-trash-can"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add Item Button */}
              <div className="mt-4 flex justify-start">
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center justify-center rounded-xl bg-white border border-blue-100 px-5 py-2.5 text-xs font-bold text-blue-600 hover:bg-blue-50 transition-all shadow-sm"
                >
                  <i className="fa fa-plus-circle mr-2"></i> {t("addAnotherPackage")}
                </button>
              </div>
            </div>

            {/* Form Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end items-center gap-4 pt-4 border-t border-blue-50/50">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showLogo}
                  onChange={(e) => setShowLogo(e.target.checked)}
                  className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-slate-600">{t("sleepingOwlsLogo")}</span>
              </label>
              <button
                type="submit"
                className="btn-soft-blue inline-flex items-center justify-center rounded-2xl px-8 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 active:scale-95 transition-transform"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa fa-circle-notch fa-spin mr-2 scale-125"></i> {t("preparingPrint")}
                  </>
                ) : (
                  <>
                    <i className="fa fa-print mr-2 text-lg"></i> {t("printGatepass")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Invoice Parser Modal (Using Existing React-Bootstrap Modal) */}
      < Modal show={showModal} onHide={handleCloseModal} size="lg" centered >
        <Modal.Header closeButton>
          <Modal.Title>{t("quickGpTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 quickgp-modal">
            <p className="text-muted">
              {t("quickGpInstructions")}
            </p>
            <Form.Group controlId="invoiceTextArea">
              <Form.Label>{t("invoiceTextLabel")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder={t("pasteInvoicePlaceholder")}
                value={invoiceText}
                onChange={(e) => setInvoiceText(e.target.value)}
              />
            </Form.Group>
            <p className="text-muted pt-2 text-italic text-small">
              {t("verifyItemsWarning")}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleParseInvoice}
            disabled={parsingLoading || !invoiceText.trim()}
          >
            {parsingLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t("parsing")}
              </>
            ) : (
              <>
                <i className="fa fa-magic me-2"></i>
                {t("getDetails")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal >
    </div >
    </AnimatedPage>
  );
}

export default GatePassForm;
